import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { Announcement, AnnouncementSeverity } from 'app/modules/admin/apps/announcements/announcements.types';
import { AnnouncementsService } from 'app/modules/admin/apps/announcements/announcements.service';
import { formatDurationMinutes, formatEventEnd, formatInLocalTZ } from 'app/modules/admin/apps/announcements/announcements.format';
import { MarkdownPipe } from 'app/shared/pipes/markdown.pipe';

@Component({
    selector: 'announcements-bell',
    templateUrl: './announcements.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'announcementsBell',
    imports: [NgClass, MatButtonModule, MatIconModule, MatTooltipModule, MarkdownPipe]
})
export class AnnouncementsBellComponent implements OnInit, OnDestroy {
    @ViewChild('announcementsOrigin') private _origin: MatButton;
    @ViewChild('announcementsPanel') private _panel: TemplateRef<any>;

    announcements: Announcement[] = [];
    unreadCount = 0;
    private _overlayRef: OverlayRef;
    private _panelOpen = false;
    private _unsubscribeAll = new Subject<void>();

    private static readonly SEVERITY_ORDER: Record<AnnouncementSeverity, number> = {
        Critical: 0,
        Warning : 1,
        Info    : 2
    };

    constructor(
        private _cdr: ChangeDetectorRef,
        private _service: AnnouncementsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef
    ) {}

    ngOnInit(): void {
        this._service.pending$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((list) => {
                this.announcements = [...(list ?? [])].sort((a, b) => {
                    const sev = AnnouncementsBellComponent.SEVERITY_ORDER[a.severity] - AnnouncementsBellComponent.SEVERITY_ORDER[b.severity];
                    if (sev !== 0) { return sev; }
                    const ta = a.startsAt ? new Date(a.startsAt).getTime() : 0;
                    const tb = b.startsAt ? new Date(b.startsAt).getTime() : 0;
                    return tb - ta;
                });
                this.unreadCount = this.announcements.filter(a => !a.seenAt).length;
                if (this._panelOpen && this.unreadCount > 0) {
                    this._markVisibleSeen();
                }
                this._cdr.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        if (this._overlayRef) { this._overlayRef.dispose(); }
    }

    openPanel(): void {
        if (!this._panel || !this._origin) { return; }
        if (!this._overlayRef) { this._createOverlay(); }
        this._overlayRef.attach(new TemplatePortal(this._panel, this._viewContainerRef));
        this._panelOpen = true;
        this._markVisibleSeen();
    }

    closePanel(): void {
        this._overlayRef?.detach();
        this._panelOpen = false;
    }

    private _markVisibleSeen(): void {
        const nowIso = new Date().toISOString();
        this.announcements
            .filter(a => !a.seenAt)
            .forEach(a => {
                a.seenAt = nowIso;
                this._service.markSeen(a.id).subscribe({ error: () => { /* ignore */ } });
            });
        this.unreadCount = 0;
        this._cdr.markForCheck();
    }

    dismiss(a: Announcement, event: MouseEvent): void {
        event.stopPropagation();
        // Optimistic: remove from local list; dismiss() also updates service state.
        this.announcements = this.announcements.filter(x => x.id !== a.id);
        this.unreadCount = this.announcements.filter(x => !x.seenAt).length;
        this._cdr.markForCheck();
        this._service.dismiss(a.id).subscribe({ error: () => { /* ignore */ } });
    }

    dismissAll(event: MouseEvent): void {
        event.stopPropagation();
        if (!this.announcements.length) { return; }
        this.announcements = [];
        this.unreadCount = 0;
        this._cdr.markForCheck();
        this._service.dismissAll().subscribe({ error: () => { /* ignore */ } });
    }

    trackByFn(_: number, a: Announcement): number { return a.id; }

    severityClasses(s: AnnouncementSeverity): string {
        switch (s) {
            case 'Critical': return 'text-red-600 bg-red-100 dark:bg-red-500 dark:text-red-50';
            case 'Warning':  return 'text-amber-600 bg-amber-100 dark:bg-amber-500 dark:text-amber-50';
            default:         return 'text-blue-600 bg-blue-100 dark:bg-blue-500 dark:text-blue-50';
        }
    }

    severityIcon(s: AnnouncementSeverity): string {
        switch (s) {
            case 'Critical': return 'heroicons_outline:exclamation-circle';
            case 'Warning':  return 'heroicons_outline:exclamation-triangle';
            default:         return 'heroicons_outline:information-circle';
        }
    }

    eventSubrow(a: Announcement): string {
        if (!a.eventStartsAt) { return ''; }
        const start = formatInLocalTZ(a.eventStartsAt);
        const dur = formatDurationMinutes(a.eventDurationMinutes);
        return dur ? `${start} · ${dur}` : start;
    }

    eventEndTooltip(a: Announcement): string {
        const end = formatEventEnd(a.eventStartsAt, a.eventDurationMinutes);
        return end ? `Ends around ${end}` : '';
    }

    private _createOverlay(): void {
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._origin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
                    { originX: 'start', originY: 'top',    overlayX: 'start', overlayY: 'bottom' },
                    { originX: 'end',   originY: 'bottom', overlayX: 'end',   overlayY: 'top' },
                    { originX: 'end',   originY: 'top',    overlayX: 'end',   overlayY: 'bottom' }
                ])
        });
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
            this._panelOpen = false;
        });
    }
}
